import { Pause, Play } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsPhone } from './use-viewport';

import type { AssetSummary } from '@/lib/assets-api';

import { IconButton } from '@/components/ui/icon-button';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { assetsApi, peekAsset } from '@/lib/assets-api';

/**
 * An image that may move, with the controls and the text equivalent that come with that.
 *
 * ## Why pausing needs a second file
 *
 * You cannot pause an `<img>`. There is no API for it and no CSS for it — an animated WebP plays
 * for as long as it is on screen. So "paused" is a different picture: the poster derivative, the
 * still first frame the server cut at upload. Every other design here follows from that one fact,
 * which is also why the accessibility requirement had to be settled before the upload pipeline was
 * written rather than after.
 *
 * ## Who each piece is for
 *
 * - **The play/pause control** — WCAG 2.2.2. A loop never ends, so it always meets the "more than
 *   five seconds" test however short one cycle is. It serves vestibular and attention needs, and
 *   it is the reason a teacher's autoplay choice can be overridden at all.
 * - **`prefers-reduced-motion`** — a device setting this reads and never stores. It WINS over the
 *   teacher's autoplay, always: a preference a viewer has set about their own body is not
 *   something an author gets to outvote. It does not hide the control, because reduced motion
 *   means "do not start without me", not "never".
 * - **The motion description** — WCAG 1.2.1. Alt text describes the poster; this describes what
 *   changes. Delivered through `aria-describedby` rather than appended to the alt, so a screen
 *   reader announces what the image IS and then what it DOES, and so the two stay separable for
 *   anything that later wants only one of them.
 *
 * ## The missing-poster case
 *
 * An asset marked animated with no poster cannot be paused, so it is rendered as an ordinary image
 * rather than given a control that would do nothing. Only reachable for an asset written before
 * posters existed, which in practice is none, since animated uploads were refused until they did.
 */
export function AnimatedImage({
  asset,
  alt,
  motionDescription,
  autoplay = true,
  still = false,
  align = 'center',
  className,
  fill,
}: {
  asset: AssetSummary;
  alt: string;
  /**
   * Required of the teacher when the asset animates; may still be empty while they are writing it.
   *
   * Omit it where the description belongs to an ANCESTOR rather than to the image — an MCQ option
   * puts its accessible name on the tile and renders the image `alt=''`, so a description wired to
   * the image would hang off something the reader never reaches.
   */
  motionDescription?: string;
  /** The teacher's choice, which governs everyone who has not asked for less motion. */
  autoplay?: boolean;
  /**
   * Render the poster and no control, for a thumbnail too small to be worth either.
   *
   * Not the same as `autoplay={false}`, which still offers to play. This is "there is no room for
   * a control here" — an option tile's compact variant is 32px, where a 36px play button is larger
   * than the picture it sits on.
   */
  still?: boolean;
  /** `start` keeps the picture against the leading edge of a flex row it shares with other
   *  content. `center` (the default) centres it, which is what a standalone image wants. */
  align?: 'center' | 'start';
  className?: string;
  /** The image is sized by its container (a bin column), so the wrapper must not shrink-wrap it. */
  fill?: boolean;
}) {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  /* 44px on a phone, which is this app's own stated touch-target size (`IconButton`'s `lg`), and
     36px with a mouse. Measured at 36 in phone landscape before this — under the guideline on the
     one pointer that needs it most, and on a control sitting on top of a picture rather than in a
     row with forgiving neighbours. */
  const isPhone = useIsPhone();

  // `still` opts out of the control, not out of being an animation — the poster is still what gets
  // drawn, so a thumbnail of a moving image shows its first frame rather than nothing.
  const canAnimate = !!asset.animated && !!asset.urls.poster && !still;
  const posterOnly = !!asset.animated && !!asset.urls.poster && still;
  const [playing, setPlaying] = useState(autoplay);

  // Re-syncs rather than only seeding: the OS setting can be switched mid-session (on iOS from
  // Control Centre), and a teacher toggling autoplay in the editor should be visible immediately
  // in the card beside it.
  useEffect(() => { setPlaying(autoplay); }, [autoplay]);

  const animating = canAnimate && playing && !reducedMotion;
  const src = animating ? asset.urls.display : (canAnimate || posterOnly ? asset.urls.poster : asset.urls.display);

  /* Composed, not described: see `animatedAltText`. `canAnimate` gates it so a still asset that
     happens to carry a stale description does not announce motion nobody will see. */
  const spokenAlt = (canAnimate && animatedAltText(alt, motionDescription, t)) || alt;

  const image = (
    <img
      src={src}
      alt={spokenAlt}
      /* An explicit signal for the accessibility report, which otherwise has only the play button
         beside this to go on — and inferring "moves" from a sibling control is the kind of coupling
         that breaks the moment the control moves. Absent, not `false`, on a still. */
      {...(canAnimate ? { 'data-animated': 'true' } : {})}
      className={className}
    />
  );

  if (!canAnimate) return image;

  return (
    /*
     * THE WRAPPER MUST HUG THE IMAGE, because the control is positioned against it.
     *
     * `flex justify-center` centred the picture and left the wrapper full-width, so `bottom-2
     * right-2` put the button at the bottom-right of the CONTAINER — off the picture entirely
     * wherever the picture was narrower, which is any image not filling its column. It looked
     * right in an MCQ option only because the image fills the tile there.
     *
     * `w-fit` shrink-wraps to the image so the corner is the image's corner, and `mx-auto` centres
     * that box — which is also what gives an animation the same position a still of the same size
     * gets, the thing the previous version was reaching for and missed.
     *
     * ## Why the centring is optional
     *
     * `margin: auto` on a FLEX ITEM absorbs the free space on its axis, so this wrapper centred the
     * picture inside whatever room was left beside its siblings. In the Image block, where the
     * wrapper is a block in a column, that is the intent. Inside an MCQ option or an Ordering row
     * — a flex row with an indicator or a grip beside it — it pushed the picture into the middle of
     * the tile, away from the control that names it. Those call sites pass `align='start'`.
     */
    /* `fill` is the one case the hug is wrong: the image is being sized BY its container, so a
       shrink-wrapped wrapper collapses it straight back to its intrinsic width and the caller's
       `w-full` does nothing. The wrapper and the image are then the same width, so the control
       still sits on the picture's own corner. */
    <span className={`relative block max-w-full ${fill ? 'w-full' : 'w-fit'} ${align === 'start' ? '' : 'mx-auto'}`}>
      {image}
      {/*
        * BOTTOM RIGHT, and lighter than it was.
        *
        * It sits on the content, which is a real cost — whatever is under it cannot be seen. The
        * bottom-right corner is the least likely place for the subject of a diagram or a photo to
        * be, and it is where a player's controls are expected. Left was simply where it landed.
        *
        * Ghosted to `/55` at rest and solid on hover or focus: enough to read as a control without
        * claiming more of the picture than it needs, and fully legible the moment it is reached
        * for. It still carries a scrim rather than going fully transparent, because an
        * IconButton's own background is legible against the app's surfaces and not against an
        * arbitrary photograph.
        */}
      <span className='absolute bottom-2 right-2 rounded-full bg-ws-surface/55 opacity-70 backdrop-blur-sm transition hover:bg-ws-surface/90 hover:opacity-100 focus-within:bg-ws-surface/90 focus-within:opacity-100'>
        <IconButton
          size={isPhone ? 'lg' : 'md'}
          aria-label={animating ? t('blocks.image.pauseAnimation') : t('blocks.image.playAnimation')}
          onClick={e => { e.stopPropagation(); setPlaying(p => !p); }}
        >
          {animating ? <Pause size={18} /> : <Play size={18} />}
        </IconButton>
      </span>
    </span>
  );
}

/**
 * Which of these assets move.
 *
 * Whether an image animates is a property of the ASSET, not of the block data — a media option
 * stores only an `assetId`. So a block that has to ask "does this option owe a motion description"
 * cannot answer it from its own data, and this is how it finds out.
 *
 * Deliberately NOT denormalised onto the option beside `kind`. A stored flag would go stale the
 * moment someone swaps an asset's bytes in the Library: replacing a still with an animation would
 * leave the option claiming it does not move, and the motion description would stop being asked
 * for silently — which is the worst way for an accessibility requirement to fail.
 *
 * THE SET'S IDENTITY IS STABLE while its contents are unchanged. Block issues are memoised on a
 * dependency array, so a fresh `Set` every render would recompute every issue on every render,
 * for every block holding a media option.
 */
/**
 * AN ANIMATION'S TEXT ALTERNATIVE: what it looks like, then what it does, in ONE string.
 *
 * ## Why not `aria-describedby`
 *
 * That was the shape, and it is the wrong home for this content. A description is announced LAST,
 * after the role and the state, and it is the first thing a reader's verbosity settings drop —
 * JAWS and NVDA both let a user switch descriptions off entirely. Acceptable for supplementary
 * detail; not for the text alternative an animation owes under WCAG 1.2.1, which is the only
 * channel a listener has for content that lives in the difference between frames.
 *
 * ## Why the words are in it
 *
 * Naming the medium inside a label is normally the redundancy every alt-text guide warns about,
 * because the role already says it. Neither objection survives here.
 *
 * "Animation" because no role says it. A real `<img>` announces as "graphic", which is not merely
 * uninformative but WRONG — it asserts a still. On an MCQ option the role is "radio button" and
 * says nothing about pictures at all, which is why "Image:" was already there.
 *
 * "Motion" because folding the two into one string removes the only thing that used to separate
 * them: the pause and change of voice a reader puts between a name and a description. Without the
 * word, a listener hears two sentences and cannot tell that the second describes CHANGE rather
 * than more of the first frame. It is not an interpretation of the picture; it is the content.
 */
export function animatedAltText(
  alt: string | undefined,
  motion: string | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  /* EMPTY when there is no motion to describe, rather than falling back to the alt on its own.
     A loose `<img>` then keeps its bare alt, which is right: its role announces "graphic", so
     nothing needs to say the content is a picture. Only `pictureName` adds that, and only for the
     elements whose role does not. */
  const plainMotion = motion?.trim() ?? '';
  if (!plainMotion) return '';
  const plainAlt = alt?.trim() ?? '';
  const clause = t('blocks.image.motionClause', { motion: plainMotion });
  return plainAlt ? `${t('blocks.image.animationAlt', { alt: plainAlt })}. ${clause}` : clause;
}

/**
 * WHAT A PICTURE IS CALLED when the element holding it does not announce that it is one.
 *
 * A loose `<img>` needs none of this: a reader says "graphic" after the alt text, so the medium is
 * already conveyed and adding the word would be the redundancy every alt-text guide warns about.
 * An MCQ option announces as "radio button" and an Ordering item as a draggable — neither says
 * anything about pictures, so for those the word has to be IN the name or no listener ever gets it.
 *
 * That is also why the emulator never prefixes anything itself: the word belongs in the markup,
 * where a real reader will say it, and the read-through then reproduces it by reading the name
 * like any other. See `SPOKEN_KINDS`.
 *
 * Returns EMPTY when there is nothing to say, so each caller keeps its own fallback — a positional
 * "Option 3" in MCQ, an item's position in Ordering.
 */
export function pictureName(
  alt: string | undefined,
  motion: string | undefined,
  animated: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (animated) {
    const withMotion = animatedAltText(alt, motion, t);
    if (withMotion) return withMotion;
    // Animated but not yet described. Still an animation, and saying so is the one thing its role
    // actively gets wrong — "graphic" asserts a still. The missing description is a defect the
    // block's own card reports; it is not a reason to call this a picture.
    const plain = alt?.trim();
    return plain ? t('blocks.image.animationAlt', { alt: plain }) : '';
  }
  const plain = alt?.trim();
  return plain ? t('blocks.image.imageAlt', { alt: plain }) : '';
}

export function useAnimatedAssetIds(assetIds: string[]): ReadonlySet<string> {
  // Joined, because the array itself is rebuilt by the caller on each render.
  const key = assetIds.join(',');
  const [ids, setIds] = useState<ReadonlySet<string>>(
    () => new Set(assetIds.filter(id => peekAsset(id)?.animated)),
  );

  useEffect(() => {
    let cancelled = false;
    const wanted = key ? key.split(',') : [];
    void Promise.all(
      // A failed lookup answers "not animated" rather than rejecting: this question only decides
      // whether to ASK for a description, and the asset's own load failure is reported where the
      // image should have been.
      wanted.map(id => assetsApi.get(id).then(a => (a.animated ? id : null)).catch(() => null)),
    ).then(found => {
      if (cancelled) return;
      const next = new Set(found.filter((id): id is string => !!id));
      setIds(prev => (prev.size === next.size && [...next].every(id => prev.has(id)) ? prev : next));
    });
    return () => { cancelled = true; };
  }, [key]);

  return ids;
}
